"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/auth/Auth";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import MyStreamWaveCurationContent from "./curations/MyStreamWaveCurationContent";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveSubmissions from "./MyStreamWaveSubmissions";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import MyStreamWaveSales from "./MyStreamWaveSales";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { useWaveEligibility } from "@/contexts/wave/WaveEligibilityContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getDropQueryKey } from "@/services/api/drop-api";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import type { ChatSubmitDropState } from "./chatSubmitDrop.types";
import { getChatSubmitDropLabels } from "./chatSubmitDrop.types";
export interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const getChatSubmitDropRestrictionMessage = ({
  dropEligibility,
  isApprovalVotingControlsLocked
}: {
  readonly dropEligibility: ReturnType<typeof getWaveDropEligibility>;
  readonly isApprovalVotingControlsLocked: boolean;
}): string | null => {
  if (!dropEligibility.canCreateDrop) {
    return dropEligibility.restrictionMessage;
  }
  if (isApprovalVotingControlsLocked) {
    return "Approval controls are locked";
  }
  return null;
};
const MyStreamWaveContent: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(143);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    isApp
  } = useDeviceInfo();
  const queryClient = useQueryClient();
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const {
    waves,
    directMessages,
    registerWave
  } = useMyStream();
  const {
    updateEligibility
  } = useWaveEligibility();
  let t1;
  if ($[0] !== isApp || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[5] = t1;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  let t4;
  if ($[8] !== registerWave || $[9] !== waveId) {
    t3 = () => {
      registerWave(waveId, true);
    };
    t4 = [registerWave, waveId];
    $[8] = registerWave;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== updateEligibility || $[13] !== wave) {
    t5 = () => {
      if (!wave) {
        return;
      }
      updateEligibility(wave.id, {
        authenticated_user_eligible_to_chat: wave.chat.authenticated_user_eligible,
        authenticated_user_eligible_to_vote: wave.voting.authenticated_user_eligible,
        authenticated_user_eligible_to_participate: wave.participation.authenticated_user_eligible,
        authenticated_user_admin: wave.wave.authenticated_user_eligible_for_admin
      });
    };
    t6 = [updateEligibility, wave];
    $[12] = updateEligibility;
    $[13] = wave;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== directMessages.list || $[17] !== waveId || $[18] !== waves.list) {
    t7 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[16] = directMessages.list;
    $[17] = waveId;
    $[18] = waves.list;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const waveFromList = t7;
  const t8 = waveFromList?.newDropsCount.count ?? 0;
  const t9 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = {
      newDropsCount: t8,
      firstUnreadSerialNo: t9
    };
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const enhancedData = t10;
  const newDropsCount = enhancedData.newDropsCount;
  let t11;
  if ($[23] !== newDropsCount || $[24] !== wave) {
    t11 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[23] = newDropsCount;
    $[24] = wave;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  useSetWaveData(t11);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  let t12;
  if ($[26] !== searchParams) {
    t12 = searchParams.get("curation");
    $[26] = searchParams;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const activeCurationId = t12;
  const {
    viewMode,
    setViewMode,
    toggleViewMode
  } = useWaveViewMode(waveId);
  const {
    isRankWave,
    isApproveWave,
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    isDm,
    isChatWave,
    participation
  } = useWave(wave);
  let t13;
  if ($[28] !== wave) {
    t13 = {
      wave
    };
    $[28] = wave;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const {
    isVotingControlsLocked: isApprovalVotingControlsLocked
  } = useApprovalWaveStatus(t13);
  const showGalleryToggle = !isRankWave && !isApproveWave && !isMemesWave && !isDm;
  let t14;
  if ($[30] !== searchParams) {
    t14 = searchParams.get("serialNo");
    $[30] = searchParams;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const hasSerialTarget = t14 !== null;
  wave?.participation.submission_strategy;
  const t15 = wave?.participation.submission_strategy ?? null;
  let t16;
  if ($[32] !== isCurationWave || $[33] !== isMemesWave || $[34] !== isQuorumWave || $[35] !== t15) {
    t16 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t15
    });
    $[32] = isCurationWave;
    $[33] = isMemesWave;
    $[34] = isQuorumWave;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const submissionExperience = t16;
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t17 = Boolean(activeProfileProxy);
  let t18;
  if ($[37] !== isCurationWave || $[38] !== isLoggedIn || $[39] !== participation || $[40] !== t17) {
    t18 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t17,
      isCurationWave,
      participation
    });
    $[37] = isCurationWave;
    $[38] = isLoggedIn;
    $[39] = participation;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const dropEligibility = t18;
  let t19;
  if ($[42] !== dropEligibility || $[43] !== isApprovalVotingControlsLocked) {
    t19 = getChatSubmitDropRestrictionMessage({
      dropEligibility,
      isApprovalVotingControlsLocked
    });
    $[42] = dropEligibility;
    $[43] = isApprovalVotingControlsLocked;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const chatSubmitDropRestrictionMessage = t19;
  const canOpenChatSubmitDrop = dropEligibility.canCreateDrop && !isApprovalVotingControlsLocked;
  const showChatSubmitDropAction = !isChatWave && !isMemesWave && submissionExperience !== WaveSubmissionExperience.MEMES_LEGACY;
  let t20;
  if ($[45] !== submissionExperience) {
    t20 = getChatSubmitDropLabels(submissionExperience);
    $[45] = submissionExperience;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const chatSubmitDropLabels = t20;
  const [chatSubmitDropState, setChatSubmitDropState] = useState(null);
  let t21;
  let t22;
  if ($[47] !== hasSerialTarget || $[48] !== setViewMode || $[49] !== showGalleryToggle || $[50] !== viewMode || $[51] !== wave) {
    t21 = () => {
      if (!wave || !hasSerialTarget || !showGalleryToggle || viewMode !== "gallery") {
        return;
      }
      setViewMode("chat");
    };
    t22 = [hasSerialTarget, setViewMode, showGalleryToggle, viewMode, wave];
    $[47] = hasSerialTarget;
    $[48] = setViewMode;
    $[49] = showGalleryToggle;
    $[50] = viewMode;
    $[51] = wave;
    $[52] = t21;
    $[53] = t22;
  } else {
    t21 = $[52];
    t22 = $[53];
  }
  useEffect(t21, t22);
  useBreakpoint();
  let t23;
  if ($[54] !== pathname || $[55] !== queryClient || $[56] !== router || $[57] !== searchParams) {
    t23 = drop => {
      queryClient.setQueryData(getDropQueryKey(drop.id), drop as ApiDrop);
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[54] = pathname;
    $[55] = queryClient;
    $[56] = router;
    $[57] = searchParams;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const onDropClick = t23;
  let t24;
  if ($[59] !== canOpenChatSubmitDrop || $[60] !== isApprovalVotingControlsLocked || $[61] !== showChatSubmitDropAction || $[62] !== submissionExperience || $[63] !== wave) {
    t24 = t25 => {
      const initialCurationUrl = t25 === undefined ? null : t25;
      if (!wave || !showChatSubmitDropAction || !canOpenChatSubmitDrop) {
        return;
      }
      setChatSubmitDropState({
        waveId: wave.id,
        submissionExperience,
        initialCurationUrl,
        isApprovalVotingControlsLocked
      });
    };
    $[59] = canOpenChatSubmitDrop;
    $[60] = isApprovalVotingControlsLocked;
    $[61] = showChatSubmitDropAction;
    $[62] = submissionExperience;
    $[63] = wave;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  const openChatSubmitDrop = t24;
  let t25;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      setChatSubmitDropState(null);
    };
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const closeChatSubmitDrop = t25;
  let t26;
  if ($[66] !== pathname || $[67] !== router || $[68] !== searchParams) {
    t26 = curationId => {
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params_1.set("curation", curationId);
      } else {
        params_1.delete("curation");
      }
      const nextQuery = params_1.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      const currentQuery = searchParams.toString();
      const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
      if (nextUrl === currentUrl) {
        return;
      }
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[66] = pathname;
    $[67] = router;
    $[68] = searchParams;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  const onSelectCuration = t26;
  if (!wave) {
    return null;
  }
  let t27;
  if ($[70] !== chatSubmitDropState || $[71] !== isApprovalVotingControlsLocked || $[72] !== submissionExperience || $[73] !== wave.id) {
    t27 = chatSubmitDropState?.waveId === wave.id && chatSubmitDropState.submissionExperience === submissionExperience && chatSubmitDropState.isApprovalVotingControlsLocked === isApprovalVotingControlsLocked ? {
      submissionExperience: chatSubmitDropState.submissionExperience,
      initialCurationUrl: chatSubmitDropState.initialCurationUrl
    } : null;
    $[70] = chatSubmitDropState;
    $[71] = isApprovalVotingControlsLocked;
    $[72] = submissionExperience;
    $[73] = wave.id;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  const activeChatSubmitDropState = t27;
  let t28;
  if ($[75] !== openChatSubmitDrop) {
    t28 = () => openChatSubmitDrop(null);
    $[75] = openChatSubmitDrop;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== canOpenChatSubmitDrop || $[78] !== chatSubmitDropLabels.compactLabel || $[79] !== chatSubmitDropLabels.label || $[80] !== chatSubmitDropRestrictionMessage || $[81] !== openChatSubmitDrop || $[82] !== showChatSubmitDropAction || $[83] !== t28) {
    t29 = {
      isVisible: showChatSubmitDropAction,
      canOpen: canOpenChatSubmitDrop,
      label: chatSubmitDropLabels.label,
      compactLabel: chatSubmitDropLabels.compactLabel,
      restrictionMessage: chatSubmitDropRestrictionMessage,
      onOpen: t28,
      onOpenWithCurationUrl: openChatSubmitDrop
    };
    $[77] = canOpenChatSubmitDrop;
    $[78] = chatSubmitDropLabels.compactLabel;
    $[79] = chatSubmitDropLabels.label;
    $[80] = chatSubmitDropRestrictionMessage;
    $[81] = openChatSubmitDrop;
    $[82] = showChatSubmitDropAction;
    $[83] = t28;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  const chatSubmitDropAction = t29;
  const t30 = showGalleryToggle ? viewMode : "chat";
  let t31;
  if ($[85] !== activeChatSubmitDropState || $[86] !== chatSubmitDropAction || $[87] !== enhancedData.firstUnreadSerialNo || $[88] !== onDropClick || $[89] !== t30 || $[90] !== wave) {
    t31 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} viewMode={t30} onDropClick={onDropClick} chatSubmitDrop={activeChatSubmitDropState} chatSubmitDropAction={chatSubmitDropAction} onCloseChatSubmitDrop={closeChatSubmitDrop} />;
    $[85] = activeChatSubmitDropState;
    $[86] = chatSubmitDropAction;
    $[87] = enhancedData.firstUnreadSerialNo;
    $[88] = onDropClick;
    $[89] = t30;
    $[90] = wave;
    $[91] = t31;
  } else {
    t31 = $[91];
  }
  let t32;
  if ($[92] !== onDropClick || $[93] !== wave) {
    t32 = <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} />;
    $[92] = onDropClick;
    $[93] = wave;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== onDropClick || $[96] !== wave) {
    t33 = <MyStreamWaveSubmissions wave={wave} onDropClick={onDropClick} />;
    $[95] = onDropClick;
    $[96] = wave;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  if ($[98] !== wave.id) {
    t34 = <MyStreamWaveSales waveId={wave.id} />;
    $[98] = wave.id;
    $[99] = t34;
  } else {
    t34 = $[99];
  }
  let t35;
  if ($[100] !== onDropClick || $[101] !== wave) {
    t35 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[100] = onDropClick;
    $[101] = wave;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== wave) {
    t36 = <MyStreamWaveOutcome wave={wave} />;
    $[103] = wave;
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  let t37;
  if ($[105] !== onDropClick || $[106] !== wave) {
    t37 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[105] = onDropClick;
    $[106] = wave;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] !== wave) {
    t38 = <MyStreamWaveFAQ wave={wave} />;
    $[108] = wave;
    $[109] = t38;
  } else {
    t38 = $[109];
  }
  let t39;
  if ($[110] !== t31 || $[111] !== t32 || $[112] !== t33 || $[113] !== t34 || $[114] !== t35 || $[115] !== t36 || $[116] !== t37 || $[117] !== t38) {
    t39 = {
      [MyStreamWaveTab.CHAT]: t31,
      [MyStreamWaveTab.LEADERBOARD]: t32,
      [MyStreamWaveTab.SUBMISSIONS]: t33,
      [MyStreamWaveTab.SALES]: t34,
      [MyStreamWaveTab.WINNERS]: t35,
      [MyStreamWaveTab.OUTCOME]: t36,
      [MyStreamWaveTab.MY_VOTES]: t37,
      [MyStreamWaveTab.FAQ]: t38
    };
    $[110] = t31;
    $[111] = t32;
    $[112] = t33;
    $[113] = t34;
    $[114] = t35;
    $[115] = t36;
    $[116] = t37;
    $[117] = t38;
    $[118] = t39;
  } else {
    t39 = $[118];
  }
  const components = t39;
  let t40;
  if ($[119] !== activeCurationId || $[120] !== chatSubmitDropAction || $[121] !== onSelectCuration || $[122] !== showGalleryToggle || $[123] !== toggleViewMode || $[124] !== viewMode || $[125] !== wave) {
    t40 = <MyStreamWaveTabs wave={wave} viewMode={viewMode} onToggleViewMode={toggleViewMode} showGalleryToggle={showGalleryToggle} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} chatSubmitDropAction={chatSubmitDropAction} />;
    $[119] = activeCurationId;
    $[120] = chatSubmitDropAction;
    $[121] = onSelectCuration;
    $[122] = showGalleryToggle;
    $[123] = toggleViewMode;
    $[124] = viewMode;
    $[125] = wave;
    $[126] = t40;
  } else {
    t40 = $[126];
  }
  let t41;
  if ($[127] !== activeContentTab || $[128] !== activeCurationId) {
    t41 = activeCurationId ? `my-stream-wave-tabpanel-curation-${activeCurationId}` : getContentTabPanelId(activeContentTab);
    $[127] = activeContentTab;
    $[128] = activeCurationId;
    $[129] = t41;
  } else {
    t41 = $[129];
  }
  let t42;
  if ($[130] !== activeContentTab || $[131] !== activeCurationId || $[132] !== components || $[133] !== onDropClick || $[134] !== wave) {
    t42 = activeCurationId ? <MyStreamWaveCurationContent key={activeCurationId} wave={wave} curationId={activeCurationId} onDropClick={onDropClick} /> : components[activeContentTab];
    $[130] = activeContentTab;
    $[131] = activeCurationId;
    $[132] = components;
    $[133] = onDropClick;
    $[134] = wave;
    $[135] = t42;
  } else {
    t42 = $[135];
  }
  let t43;
  if ($[136] !== t41 || $[137] !== t42) {
    t43 = <div className="tw-relative tw-min-h-0 tw-min-w-0 tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t41}>{t42}</div>;
    $[136] = t41;
    $[137] = t42;
    $[138] = t43;
  } else {
    t43 = $[138];
  }
  let t44;
  if ($[139] !== stableWaveKey || $[140] !== t40 || $[141] !== t43) {
    t44 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col" key={stableWaveKey}>{t40}{t43}</div>;
    $[139] = stableWaveKey;
    $[140] = t40;
    $[141] = t43;
    $[142] = t44;
  } else {
    t44 = $[142];
  }
  return t44;
};
export default MyStreamWaveContent;